import {
  GRAFTCODE_ONBOARDING_GOAL,
  GRAFTCODE_ONBOARDING_SCENARIO,
  type GraftcodeOnboardingGoalId,
  type GraftcodeOnboardingScenarioId,
  type GraftcodeOnboardingSetupMode,
} from '../graftcode-onboarding.const'
import type { GraftcodeOnboardingDashboardCourse } from '../graftcode-onboarding-dashboard-courses.const'
import { GRAFTCODE_ONBOARDING_DASHBOARD_COURSES } from '../graftcode-onboarding-dashboard-courses.const'
import { getStackLabel } from '../graftcode-onboarding-stacks.const'
import { getOnboardingStacksForScenario } from '../lib/buildOnboardingStackOptions'

/** Suggested next scenarios after the user-s anchor tutorial (first pick). */
export const ONBOARDING_RECOMMENDED_NEXT_BY_SCENARIO: Record<
  GraftcodeOnboardingScenarioId,
  readonly GraftcodeOnboardingScenarioId[]
> = {
  [GRAFTCODE_ONBOARDING_SCENARIO.frontend]: [
    GRAFTCODE_ONBOARDING_SCENARIO.backend,
    GRAFTCODE_ONBOARDING_SCENARIO.modules,
    GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    GRAFTCODE_ONBOARDING_SCENARIO.mcp,
    GRAFTCODE_ONBOARDING_SCENARIO.monolith,
  ],
  [GRAFTCODE_ONBOARDING_SCENARIO.backend]: [
    GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    GRAFTCODE_ONBOARDING_SCENARIO.mcp,
    GRAFTCODE_ONBOARDING_SCENARIO.modules,
    GRAFTCODE_ONBOARDING_SCENARIO.frontend,
    GRAFTCODE_ONBOARDING_SCENARIO.monolith,
  ],
  [GRAFTCODE_ONBOARDING_SCENARIO.microservices]: [
    GRAFTCODE_ONBOARDING_SCENARIO.monolith,
    GRAFTCODE_ONBOARDING_SCENARIO.modules,
    GRAFTCODE_ONBOARDING_SCENARIO.backend,
    GRAFTCODE_ONBOARDING_SCENARIO.mcp,
    GRAFTCODE_ONBOARDING_SCENARIO.frontend,
  ],
  [GRAFTCODE_ONBOARDING_SCENARIO.modules]: [
    GRAFTCODE_ONBOARDING_SCENARIO.backend,
    GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    GRAFTCODE_ONBOARDING_SCENARIO.frontend,
    GRAFTCODE_ONBOARDING_SCENARIO.monolith,
    GRAFTCODE_ONBOARDING_SCENARIO.mcp,
  ],
  [GRAFTCODE_ONBOARDING_SCENARIO.mcp]: [
    GRAFTCODE_ONBOARDING_SCENARIO.backend,
    GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    GRAFTCODE_ONBOARDING_SCENARIO.modules,
    GRAFTCODE_ONBOARDING_SCENARIO.monolith,
    GRAFTCODE_ONBOARDING_SCENARIO.frontend,
  ],
  [GRAFTCODE_ONBOARDING_SCENARIO.monolith]: [
    GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    GRAFTCODE_ONBOARDING_SCENARIO.modules,
    GRAFTCODE_ONBOARDING_SCENARIO.backend,
    GRAFTCODE_ONBOARDING_SCENARIO.mcp,
    GRAFTCODE_ONBOARDING_SCENARIO.frontend,
  ],
}

export type PersonalizedOnboardingCourse = GraftcodeOnboardingDashboardCourse & {
  isRecommended: boolean
  recommendationScore: number
  displayStackId: string | null
  stackLabel: string | null
}

export function resolveDisplayStackForOnboardingCourse(
  scenarioId: GraftcodeOnboardingScenarioId,
  setupMode: GraftcodeOnboardingSetupMode,
  preferredStackId: string | null
): { displayStackId: string | null; stackLabel: string | null } {
  if (!preferredStackId) {
    return { displayStackId: null, stackLabel: null }
  }
  const stacks = getOnboardingStacksForScenario(scenarioId, setupMode)
  if (!stacks.some((stack) => stack.id === preferredStackId)) {
    return { displayStackId: null, stackLabel: null }
  }
  return {
    displayStackId: preferredStackId,
    stackLabel: getStackLabel(preferredStackId, scenarioId),
  }
}

export type PersonalizeOnboardingCoursesInput = {
  goalId: GraftcodeOnboardingGoalId
  setupMode: GraftcodeOnboardingSetupMode
  primaryScenarioId: GraftcodeOnboardingScenarioId | null
  lastCompletedStackId: string | null
  completedScenarioIds: readonly GraftcodeOnboardingScenarioId[]
  completedStacksByScenario?: Partial<Record<GraftcodeOnboardingScenarioId, string>>
}

function scenarioSupportsStack(
  scenarioId: GraftcodeOnboardingScenarioId,
  stackId: string,
  setupMode: GraftcodeOnboardingSetupMode
): boolean {
  return getOnboardingStacksForScenario(scenarioId, setupMode).some((stack) => stack.id === stackId)
}

const SCORE_COMPLETED = -1000
const SCORE_STACK_MATCH = 1000
const SCORE_SCENARIO_PATH_BASE = 48
const SCORE_SCENARIO_PATH_STEP = 8

export function scoreScenarioPathFromAnchor(
  scenarioId: GraftcodeOnboardingScenarioId,
  anchor: GraftcodeOnboardingScenarioId | null
): number {
  if (!anchor) {
    return 0
  }
  const nextIds = ONBOARDING_RECOMMENDED_NEXT_BY_SCENARIO[anchor] ?? []
  const rank = nextIds.indexOf(scenarioId)
  if (rank < 0) {
    return 0
  }
  return SCORE_SCENARIO_PATH_BASE - rank * SCORE_SCENARIO_PATH_STEP
}

export function scoreOnboardingCourseRecommendation(
  scenarioId: GraftcodeOnboardingScenarioId,
  input: PersonalizeOnboardingCoursesInput
): number {
  const completed = new Set(input.completedScenarioIds)
  if (completed.has(scenarioId)) {
    return SCORE_COMPLETED
  }

  const anchor = input.primaryScenarioId ?? input.completedScenarioIds[input.completedScenarioIds.length - 1] ?? null

  const supportsLastStack =
    Boolean(input.lastCompletedStackId) &&
    scenarioSupportsStack(scenarioId, input.lastCompletedStackId!, input.setupMode)

  let score = supportsLastStack ? SCORE_STACK_MATCH : 0
  score += scoreScenarioPathFromAnchor(scenarioId, anchor)

  if (input.goalId === GRAFTCODE_ONBOARDING_GOAL.ai) {
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.mcp) {
      score += 6
    }
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.backend) {
      score += 3
    }
  }

  if (input.goalId === GRAFTCODE_ONBOARDING_GOAL.integrations) {
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.frontend) {
      score += 5
    }
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.microservices) {
      score += 4
    }
  }

  return score
}

export function getPersonalizedOnboardingCourses(
  input: PersonalizeOnboardingCoursesInput,
  courses: readonly GraftcodeOnboardingDashboardCourse[] = GRAFTCODE_ONBOARDING_DASHBOARD_COURSES
): PersonalizedOnboardingCourse[] {
  const scored = courses.map((course, index) => {
    const recommendationScore = scoreOnboardingCourseRecommendation(course.id, input)
    const isCompleted = input.completedScenarioIds.includes(course.id)
    const stackIdForDisplay = isCompleted
      ? (input.completedStacksByScenario?.[course.id] ?? input.lastCompletedStackId)
      : input.lastCompletedStackId
    const stackDisplay = resolveDisplayStackForOnboardingCourse(course.id, input.setupMode, stackIdForDisplay ?? null)
    return {
      ...course,
      ...stackDisplay,
      recommendationScore,
      baseOrder: index,
      isRecommended: false,
    }
  })

  const completionIndex = (scenarioId: GraftcodeOnboardingScenarioId) => input.completedScenarioIds.indexOf(scenarioId)

  scored.sort((left, right) => {
    const leftDone = left.recommendationScore < 0
    const rightDone = right.recommendationScore < 0
    if (leftDone !== rightDone) {
      return leftDone ? -1 : 1
    }
    if (leftDone && rightDone) {
      return completionIndex(right.id) - completionIndex(left.id)
    }
    if (right.recommendationScore !== left.recommendationScore) {
      return right.recommendationScore - left.recommendationScore
    }
    return left.baseOrder - right.baseOrder
  })

  let recommendedSlots = 2
  for (const course of scored) {
    if (recommendedSlots <= 0) {
      break
    }
    if (course.recommendationScore <= 0) {
      continue
    }
    course.isRecommended = true
    recommendedSlots -= 1
  }

  return scored.map((course) => {
    const { baseOrder, ...withoutBaseOrder } = course
    void baseOrder
    return withoutBaseOrder
  })
}

export function resolvePreferredStackForOnboardingScenario(
  scenarioId: GraftcodeOnboardingScenarioId,
  setupMode: GraftcodeOnboardingSetupMode,
  preferredStackId: string | null,
  completedStacksByScenario: Partial<Record<GraftcodeOnboardingScenarioId, string>>
): string {
  const stacks = getOnboardingStacksForScenario(scenarioId, setupMode)
  const fromThisScenario = completedStacksByScenario[scenarioId]
  if (fromThisScenario && stacks.some((stack) => stack.id === fromThisScenario)) {
    return fromThisScenario
  }
  if (preferredStackId && stacks.some((stack) => stack.id === preferredStackId)) {
    return preferredStackId
  }
  const scenario = stacks[0]
  return scenario?.id ?? preferredStackId ?? 'javascript'
}
